import React, { useState, useEffect, useContext, useRef } from 'react';
import { RouteComponentProps } from 'react-router';

import { VenueTemplateForm } from '@go-client/components';
import { camelToSentenceCase, capitalize } from '@go-shared/lib/string';
import {
  bare,
  create,
  update,
  exportVenueTemplatesToCSV,
  setArchived,
  uploadVenueImage,
  paginateVenueTemplates,
} from '@go-client/lib/api/venueTemplate';
import { VenueTemplate, BareVenueTemplate } from '@go-shared/types/venueTemplate';

import './VenueLibrary.scss';
import { Popup, Alert, List } from '@spark-labs/avocado-ui';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { getVenueLocation } from '@go-shared/lib/venue';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { getUsers } from '@go-client/lib/api/user';
import { User } from '@go-shared/types/user';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import {
  BubbleBadge,
  BubbleButton,
  BubbleDebounce,
  BubbleFormGroup,
  BubbleInput,
  BubbleOverlay,
  BubbleTable,
  BubbleTypeahead,
  BubbleAccordion,
} from '@go-client/components/BubbleUI';
import { PartialBy } from '@go-shared/types/typeUtils';
import { canManageLibraries } from '@go-shared/lib/permissions';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { getCommonTexts } from '@go-shared/lib/brand';
import { LibrariesSubNavigation } from '@go-client/components/Navigations/LibrariesSubNavigation';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';

import Libraries from '@go-client/pages/Libraries/Libraries';
import { hashStringToColor } from '@go-shared/lib/utils';
import { CopyToClipboardButton } from '@go-client/components/CopyToClipboard';
import { SortOrder } from '@go-shared/types/userActionRecord';
import { DEFAULT_PER_PAGE, PaginationMetadata } from '@go-shared/types/pagination';
import { BubbleTablePaginationFooter } from '@go-client/components/BubbleUI/BubbleTablePaginationFooter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSortingIcon } from '@go-shared/lib/paginations';
import { getLocaleUTCDateTimeString } from '@go-shared/lib/time';
import { McmEndpoints } from '@go-client/components/Mcm/McmEndpoints';
import { readQs } from '@go-shared/lib/querystring';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import { BrandVenueType } from '@go-shared/types/brand';

interface VenueLibraryDataBundle {
  venueTemplates: VenueTableItem[];
  users: User[];
}

type VenueTableItem = {
  id: string;
  name: string;
  _source: VenueTemplate;
};

type VisibleFields = {
  [key: string]: boolean;
};

type VenueTableFields = BubbleTableFields<VenueTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

type VenueTemplateFilters = {
  types: Array<BubbleTypeaheadValue<BrandVenueType>>;
};

type PaginationParams<T> = Partial<VenueTemplateFilters> & {
  page: number;
  perPage: number;
  sortBy: keyof T;
  sortDirection: SortOrder;
  archived: boolean;
  startDate?: string;
  endDate?: string;
  propertyId?: string;
  search?: string;
};

export default function VenueLibrary(props: RouteComponentProps) {
  const { ...rest } = props;

  const { user } = useUserInfo();
  const { activeProperty: property } = useActiveProperty();
  const { commonTextTranslations } = useContext(FeedEditorCtx);
  const serverInfo = useServerInfo();
  const { brand, defaultLanguage } = serverInfo;

  const typeOpts = brand.venueTypes
    .sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      } else if (a.name > b.name) {
        return 1;
      }
      return 0;
    })
    .map(vt => ({ key: vt.name, item: vt }));

  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(true);

  const [dataBundle, setDataBundle] = useState<VenueLibraryDataBundle | undefined>(undefined);
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>(
    undefined
  );

  const [showVenueTemplateForm, setShowVenueTemplateForm] = useState(false);
  const [venueTemplateFormMode, setVenueTemplateFormMode] = useState<'create' | 'edit' | undefined>(
    undefined
  );
  const [selectedVenueTemplate, setSelectedVenueTemplate] = useState<VenueTemplate | undefined>(
    undefined
  );
  const [filters, setFilters] = useState<VenueTemplateFilters>({
    types: [],
  });

  const [visibleFields, setVisibleFields] = useState<VisibleFields>({
    name: true,
    level: true,
    occupancy: false,
    types: true,
    configurations: false,
    bookable: false,
    private: true,
    images: true,
    createdAt: false,
    actions: true,
    endpoints: true,
  });

  const queries = readQs();

  const [paginationParams, setPaginationParams] = useState<PaginationParams<BareVenueTemplate>>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    sortBy: 'createdAt',
    sortDirection: SortOrder.DESC,
    startDate: '',
    endDate: '',
    archived: false,
    propertyId: property._id,
    search: typeof queries.search === 'string' ? queries.search : '',
    types: [],
  });

  const contentRef = useRef<HTMLDivElement | null>(null);
  // this will scroll the page to top when search is modified
  useEffect(() => {
    // We want to scroll to top only when we are in page 1 or paginationParams are reset
    if (paginationParams.page !== 1) {
      return;
    }

    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [paginationParams, contentRef]);

  useEffect(() => {
    if (property._id !== paginationParams.propertyId) {
      setPaginationParams({ ...paginationParams, propertyId: property._id });
    }
  }, [property]);

  async function refresh() {
    setLoading(true);
    await fetchDataBundle();
  }

  async function refreshAfterBookingHourEdit() {
    setShowVenueTemplateForm(false);
    setLoading(true);
    await fetchDataBundle();
  }

  useEffect(() => {
    refresh();
  }, [paginationParams]);

  const commonTexts = getCommonTexts(
    {
      default: brand.commonTexts,
      translations: commonTextTranslations,
    },
    { selected: defaultLanguage.key }
  );

  async function fetchDataBundle() {
    setLoading(true);
    try {
      const [users, venueTemplates] = await Promise.all([
        getUsers(property._id),
        paginateVenueTemplates({
          page: paginationParams.page,
          perPage: paginationParams.perPage,
          sortBy: paginationParams.sortBy,
          sortDirection: paginationParams.sortDirection,
          propertyId: property._id,
          ...(paginationParams.search && { search: paginationParams.search }),
          ...(paginationParams.archived && { archived: paginationParams.archived }),
          ...(paginationParams.startDate && { startDate: paginationParams.startDate }),
          ...(paginationParams.endDate && { endDate: paginationParams.endDate }),
          ...(paginationParams?.types && {
            types: paginationParams.types.map(t => t.item._id),
          }),
        }),
      ]);

      setPaginationMetadata(venueTemplates.meta);

      const vt = venueTemplates.data.map(vt => {
        return {
          id: vt._id,
          name: vt.name,
          _source: vt,
        };
      });

      setDataBundle({
        users, // Users are in use?
        venueTemplates:
          paginationParams.page > 1 ? [...(dataBundle?.venueTemplates ?? []), ...vt] : vt,
      });
    } finally {
      setLoading(false);
    }
  }

  function openCreateVenueTemplateForm() {
    let newVenueTemplate = {
      _id: '',
      name: '',
      description: '',
      images: [],
      subvenues: [],
      occupancy: 0,
      types: [],
      level: 0,
      configurations: [],
      order: 0,
      bookingHours: [],
      location: undefined,
      property,
      callToActions: [],
    };

    setShowVenueTemplateForm(true);
    setSelectedVenueTemplate(newVenueTemplate);
    setVenueTemplateFormMode('create');
  }

  function openUpdateVenueTemplateForm(venueTemplate: VenueTemplate) {
    setShowVenueTemplateForm(true);
    setSelectedVenueTemplate(venueTemplate);
    setVenueTemplateFormMode('edit');
  }

  async function generateCSV() {
    const result = await exportVenueTemplatesToCSV(property._id);
    if (!result.location) return;
    window.open(result.location, '_blank');
  }

  async function handleFormSubmit(venueTemplate: VenueTemplate, files?: File[]) {
    const bareVenueTemplate = bare(venueTemplate);

    if (venueTemplateFormMode === 'edit') {
      await update(bareVenueTemplate);
    } else if (venueTemplateFormMode === 'create') {
      let newVenueTemplate: PartialBy<BareVenueTemplate, '_id'> = { ...bareVenueTemplate };
      delete newVenueTemplate._id;
      const vt = await create(newVenueTemplate);

      if (files?.length) {
        const images = await Promise.all(
          files.map(file => uploadVenueImage({ ...venueTemplate, _id: vt._id }, file))
        );

        if (images.length) {
          await update({ ...vt, images: images.map(image => image.location) });
        }
      }
    }

    closeVenueTemplateForm(false);
    refresh();
  }

  async function handleEditedVenueChange() {
    if (isSaved) {
      setIsSaved(false);
    }
  }

  async function closeVenueTemplateForm(showPrompt = true) {
    let shouldExit = true;

    if (!isSaved && showPrompt) {
      shouldExit = await Alert.question('You have unsaved changes, do you want to exit anyways?');
    }

    if (shouldExit) {
      setShowVenueTemplateForm(false);
      setIsSaved(true);
    }
  }

  async function handleFormArchive() {
    if (!selectedVenueTemplate) return;

    let shouldContinue = await Alert.question(
      `Are you sure you want to ${selectedVenueTemplate?.archived ? 'unarchive' : 'archive'} "${
        selectedVenueTemplate.name
      }"?`
    );

    if (shouldContinue) {
      await setArchived({
        ...selectedVenueTemplate,
        archived: !selectedVenueTemplate.archived,
      });

      closeVenueTemplateForm();

      // no need to re-fetch, only remove the archived one from list
      setDataBundle({
        ...dataBundle,
        venueTemplates: (dataBundle?.venueTemplates || []).filter(
          vt => vt._source._id !== selectedVenueTemplate._id
        ),
        users: dataBundle?.users || [],
      });
    }
  }

  const popupAction = venueTemplateFormMode ? capitalize(venueTemplateFormMode) + ' Venue' : '';
  const venueTemplateTitle = selectedVenueTemplate ? selectedVenueTemplate.name : '';
  const popupTitle = `${popupAction}  ${
    venueTemplateFormMode === 'edit' ? '- ' + venueTemplateTitle : ''
  }`;

  const showLocation = getPropertyConfiguration(property.type, 'showLocation');
  const showNewVenueBtn = canManageLibraries(user);

  const tableFields: Array<VenueTableFields> = [
    {
      key: 'name',
      tdProps: { style: { minWidth: '200px' } },
      _toggleable: true,
    },
    {
      key: 'level',
      _toggleable: true,
    },
    {
      key: 'occupancy',
      _toggleable: true,
    },
    {
      key: 'types',
      _toggleable: true,
    },
    {
      key: 'endpoints',
      _toggleable: true,
    },
    {
      key: 'configurations',
      _toggleable: true,
    },
    {
      key: 'bookable',
      _toggleable: true,
    },
    {
      key: 'private',
      _toggleable: true,
    },
    {
      key: 'images',
      _toggleable: true,
    },
    {
      key: 'createdAt',
      label: 'Created',
      behavior: BubbleTableFieldBehavior.Condense,
      _toggleable: true,
      thProps: {
        onClick: () => {
          const sortDirection =
            paginationParams.sortBy === 'createdAt'
              ? paginationParams.sortDirection * -1
              : SortOrder.DESC;
          setPaginationParams({ ...paginationParams, page: 1, sortBy: 'createdAt', sortDirection });
        },
        className: 'whitespace-nowrap',
      },
      render: () => (
        <>
          <span className="cursor-pointer">
            Created
            {paginationParams.sortBy === 'createdAt' && (
              <FontAwesomeIcon
                icon={getSortingIcon(paginationParams.sortDirection)}
                fixedWidth
                size={'sm'}
                className={'cursor-pointer'}
              />
            )}
          </span>
        </>
      ),
    },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<VenueTableItem>> = [
    {
      key: 'name',
      render: item => {
        return (
          <>
            <div>{item.name}</div>
            {item.id.length > 0 && <CopyToClipboardButton copyText={item.id} label="Copy ID" />}
          </>
        );
      },
    },
    {
      key: 'types',
      render: item => {
        let element = <></>;
        if (item._source.types.length > 0) {
          element = (
            <>
              {item._source.types.map(t => {
                return (
                  <BubbleBadge key={t._id} color={hashStringToColor(t.name)} className="mr-1">
                    {t.name}
                  </BubbleBadge>
                );
              })}
            </>
          );
        }
        return element;
      },
    },
    {
      key: 'level',
      render: item => {
        let location = showLocation ? getVenueLocation(item._source, commonTexts) : '';
        return <div>{`${item._source.level} ${location}`}</div>;
      },
    },
    {
      key: 'occupancy',
      render: item => <div>{item._source.occupancy}</div>,
    },
    {
      key: 'configurations',
      render: item => {
        let element = <></>;
        if (item._source.configurations.length) {
          let validConfigurations = item._source.configurations.filter(c => c.name !== 'default');
          if (validConfigurations.length > 0) {
            element = (
              <>
                <List elements={validConfigurations.map(c => `${c.name}: ${c.occupancy}`)} />
              </>
            );
          }
        }

        return element;
      },
    },
    {
      key: 'endpoints',
      render: item => <McmEndpoints template={item._source} />,
    },
    {
      key: 'private',
      render: item => {
        let element = <></>;
        if (item._source.isHidden) {
          element = (
            <>
              <FontAwesomeIcon icon="check" fixedWidth className="text-slate" />
            </>
          );
        }
        return element;
      },
    },
    {
      key: 'bookable',
      render: item => {
        let element = <></>;
        if (!item._source.isPrivate) {
          element = <FontAwesomeIcon fixedWidth icon="check" className="text-slate" />;
        }
        return element;
      },
    },
    {
      key: 'images',
      render: item => {
        return <>{item._source.images.length}</>;
      },
    },

    {
      key: 'createdAt',
      render: item => {
        let element = <></>;

        if (item._source.createdAt) {
          const createdAt = getLocaleUTCDateTimeString(new Date(item._source.createdAt));
          const [date, time] = createdAt.split(', ');
          element = (
            <>
              <div className="block whitespace-nowrap">{date}</div>
              <div className="block whitespace-nowrap">{time}</div>
            </>
          );
        }

        return element;
      },
    },
    {
      key: 'actions',
      render: item => (
        <BubbleButton
          className="p-2-i"
          variant="navy"
          inverse
          icon="edit"
          onClick={() => openUpdateVenueTemplateForm(item._source)}
        />
      ),
    },
  ];

  const visibleTableFields = tableFields.filter(tf =>
    tf._visible !== undefined ? tf._visible : visibleFields[tf.key]
  );
  const toggleableTableFields = tableFields.filter(tf => tf._toggleable);

  function headerFn() {
    return <LibrariesSubNavigation active="venues" />;
  }

  function bodyFn() {
    return (
      <BubbleOverlay loading={loading}>
        <BubbleTable
          responsive
          striped
          headVariant="titanium"
          fields={visibleTableFields}
          items={dataBundle?.venueTemplates}
          keyExtractor={item => item._source._id}
          stickyHeader={182}
          templates={tableTemplates}
          showPretitle
          pretitleClassName="p-0-i"
          showAllItems={true}
          onRowDblClick={item => openUpdateVenueTemplateForm(item._source)}
          pretitleRender={() => (
            <div className="px-4 pt-4 pb-1">
              <BubbleDebounce
                onTrigger={[
                  (search: string) => setPaginationParams({ ...paginationParams, page: 1, search }),
                  500,
                ]}
              >
                {({ debounce }) => (
                  <BubbleInput
                    type="text"
                    variant="titanium"
                    block
                    value={paginationParams.search}
                    inputClassName="outline-none-i"
                    onValue={debounce}
                    placeholder="Search..."
                  />
                )}
              </BubbleDebounce>
            </div>
          )}
        />
        {paginationMetadata && (
          <BubbleTablePaginationFooter
            paginationMetadata={paginationMetadata}
            perPage={paginationParams.perPage}
            onLoadMoreClick={() =>
              setPaginationParams({ ...paginationParams, page: paginationParams.page + 1 })
            }
            onPerPageChange={value =>
              setPaginationParams({ ...paginationParams, page: 1, perPage: value })
            }
          />
        )}
      </BubbleOverlay>
    );
  }

  function outerFn() {
    return (
      showVenueTemplateForm && (
        <Popup title={popupTitle} onClose={closeVenueTemplateForm}>
          {selectedVenueTemplate && (
            <VenueTemplateForm
              user={user}
              users={dataBundle?.users || []}
              serverInfo={serverInfo}
              propertyType={property.type}
              venueTemplate={selectedVenueTemplate}
              onSubmit={handleFormSubmit}
              onArchive={handleFormArchive}
              showNewVenueBtn={showNewVenueBtn}
              onEditedVenueChange={handleEditedVenueChange}
              refresh={refreshAfterBookingHourEdit}
            />
          )}
        </Popup>
      )
    );
  }

  function sideFn() {
    return (
      <>
        <div className="flex flex-col relative items-stretch p-4 bg-titanium-darker">
          <div className="grid gap-2">
            {showNewVenueBtn && (
              <BubbleButton
                onClick={openCreateVenueTemplateForm}
                color="green"
                variant="lime"
                className="text-left"
                icon="sparkles"
              >
                Create Venue
              </BubbleButton>
            )}
            <BubbleButton
              variant="slate"
              onClick={generateCSV}
              className="text-left"
              icon="file-export"
            >
              Export to CSV
            </BubbleButton>
          </div>
        </div>
        <BubbleAccordion
          variant="titanium"
          className="flex-grow flex flex-col border-t border-forest-darkest"
          labels={['Filters']}
          open={[0]}
          locked={[0]}
        >
          <div className="grid gap-4 p-4">
            <BubbleFormGroup label="Dates" labelClassName="text-white-i">
              <div className="grid grid-cols-1 gap-1">
                <BubbleDebounce
                  onTrigger={[
                    (date: string) => {
                      setPaginationParams({
                        ...paginationParams,
                        page: 1,
                        startDate: date,
                      });
                    },
                    500,
                  ]}
                >
                  {({ debounce }) => (
                    <BubbleInput
                      variant="titanium"
                      type="date"
                      value={paginationParams.startDate}
                      onValue={debounce}
                    />
                  )}
                </BubbleDebounce>
                <BubbleDebounce
                  onTrigger={[
                    (date: string) =>
                      setPaginationParams({ ...paginationParams, page: 1, endDate: date }),
                    500,
                  ]}
                >
                  {({ debounce }) => (
                    <BubbleInput
                      variant="titanium"
                      type="date"
                      value={paginationParams.endDate}
                      onValue={debounce}
                    />
                  )}
                </BubbleDebounce>
              </div>
            </BubbleFormGroup>

            <BubbleFormGroup label="Options" labelClassName="text-white-i">
              <div className="grid gap-1">
                <div className="text-white flex relative">
                  <BubbleInput
                    type="checkbox"
                    variant="titanium"
                    checked={paginationParams.archived}
                    inputClassName="stretched-link"
                    className="mr-1"
                    onValue={(checked: boolean) => {
                      setPaginationParams({ ...paginationParams, page: 1, archived: checked });
                    }}
                  />
                  <span>View Archived</span>
                </div>
              </div>
            </BubbleFormGroup>

            <BubbleFormGroup label="Type" labelClassName="text-white-i">
              <BubbleTypeahead
                block
                variant="titanium"
                placeholder="Filter types shown..."
                menuClassName="max-h-80 overflow-y-auto"
                value={filters.types}
                options={typeOpts}
                onAdd={t => {
                  const types = [...filters.types, t];

                  setFilters({ ...filters, types });
                  setPaginationParams({ ...paginationParams, page: 1, types });
                }}
                onRemove={t => {
                  const types = filters.types.filter(t2 => t.key !== t2.key);

                  setFilters({ ...filters, types });
                  setPaginationParams({ ...paginationParams, page: 1, types });
                }}
                onClearAll={() => {
                  setFilters({ ...filters, types: [] });
                  setPaginationParams({ ...paginationParams, page: 1, types: [] });
                }}
              />
            </BubbleFormGroup>

            <BubbleFormGroup label="Fields" labelClassName="text-white-i">
              <div className="grid grid-cols-1">
                {toggleableTableFields.map(tf => (
                  <div key={tf.key} className="text-white flex relative py-1">
                    <BubbleInput
                      type="checkbox"
                      variant="titanium"
                      className="mr-1"
                      inputClassName="stretched-link"
                      checked={visibleFields[tf.key]}
                      onValue={chk => setVisibleFields({ ...visibleFields, [tf.key]: chk })}
                    />
                    <span>{tf.label || camelToSentenceCase(tf.key)}</span>
                  </div>
                ))}
              </div>
            </BubbleFormGroup>
          </div>
        </BubbleAccordion>
      </>
    );
  }

  return (
    <Libraries
      user={user}
      active="libraries"
      property={property}
      loading={loading}
      contentRef={contentRef}
      headerFn={headerFn}
      bodyFn={bodyFn}
      outerFn={outerFn}
      sideFn={sideFn}
      {...rest}
    />
  );
}
